import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    barValueLabel,
    compactMoney,
    formatMoney,
    formatTrendMonth,
    INCOME_MODE_LABELS,
    therapistLabel,
} from '../../../library/dashboardDisplay';
import {
    IncomeMode,
    ITherapistTrend,
    ITrendPoint,
    therapistSeriesId,
    trendIncomeOf,
} from '../../../library/models/clinicDashboard';
import { useSeriesVisibility } from '../../../library/seriesVisibility';
import ResourcesIds from '../../../resources/ResourcesIds';
import { therapistSeriesColor, TOTAL_SERIES_COLOR } from '../../../theme/chartPalette';
import { AboveBarLabel } from '../../dashboard/AboveBarLabel';
import { ChartLegend, ChartLegendItem } from '../../dashboard/ChartLegend';
import { ChartScroller } from '../../dashboard/ChartScroller';
import {
    chartCardStyles,
    chartEmptyBoxStyles,
    chartSubtitleStyles,
    chartTitleStyles,
} from '../../dashboard/monthlyChart.styles';
import {
    axisMaxWithHeadroom,
    BAR_BORDER_RADIUS,
    BAR_GAP_RATIO,
    CATEGORY_GAP_RATIO,
    chartHeightFor,
    labelFontSizeFor,
    monthTickInterval,
    tickLabelStyleFor,
} from '../../dashboard/monthlyChartConfig';

const TOTAL_SERIES_ID = 'TOTAL';

interface MonthlyIncomeChartProps {
    trend: ITrendPoint[];
    therapistTrend: ITherapistTrend[];
    mode: IncomeMode;
}

/**
 * The clinic's income month by month: the whole practice, and each therapist beside it.
 *
 * The Billed/Earned toggle changes only which of the two figures every bar reads. It does
 * not refetch, does not reorder the series, and does not touch the colours -- series stay
 * in the order the API sent them, which is by therapist id, precisely so that a therapist
 * keeps their colour when the reader switches between the two readings or hides a
 * colleague. Ordering by size belongs to the table underneath, where nothing is coloured.
 */
export const MonthlyIncomeChart = ({
    trend,
    therapistTrend,
    mode,
}: MonthlyIncomeChartProps): JSX.Element => {
    const theme = useTheme();
    const { t: translate, i18n } = useTranslation();
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

    const months: string[] = trend.map(point => formatTrendMonth(point.month, i18n));

    /** Total first, then each therapist in the API's stable order. */
    const allSeries = useMemo(
        () => [
            {
                id: TOTAL_SERIES_ID,
                label: translate(ResourcesIds.DASHBOARD_SERIES_TOTAL),
                color: TOTAL_SERIES_COLOR,
                data: trend.map(point => trendIncomeOf(point, mode)),
            },
            ...therapistTrend.map((series, index) => ({
                id: therapistSeriesId(series),
                label: therapistLabel(series, translate),
                color: therapistSeriesColor(index),
                data: series.points.map(point => trendIncomeOf(point, mode)),
            })),
        ],
        [trend, therapistTrend, mode, translate]
    );

    const seriesIds: string[] = useMemo(() => allSeries.map(series => series.id), [allSeries]);
    const { hidden, toggle } = useSeriesVisibility(seriesIds);

    const visible = allSeries.filter(series => !hidden.has(series.id));
    const legendItems: ChartLegendItem[] = allSeries.map(({ id, label, color }) => ({ id, label, color }));

    const hasIncome: boolean = allSeries.some(series => series.data.some(value => value > 0));

    return (
        <Card sx={chartCardStyles}>
            <Typography variant="h6" sx={chartTitleStyles}>
                {translate(ResourcesIds.DASHBOARD_CHART_INCOME_TITLE)}
            </Typography>

            <Typography variant="caption" sx={chartSubtitleStyles}>
                {translate(ResourcesIds.DASHBOARD_CHART_INCOME_SUBTITLE)}
            </Typography>

            {!hasIncome ? (
                <Box sx={chartEmptyBoxStyles}>
                    <Typography variant="body2">
                        {translate(ResourcesIds.DASHBOARD_CHART_EMPTY)}
                    </Typography>
                </Box>
            ) : (
                <>
                    <ChartLegend
                        items={legendItems}
                        hidden={hidden}
                        onToggle={toggle}
                        groupLabel={translate(ResourcesIds.DASHBOARD_LEGEND_LABEL)}
                    />

                    <ChartScroller
                        visibleSeries={visible.length}
                        label={translate(ResourcesIds.DASHBOARD_CHART_INCOME_TITLE)}
                    >
                        <BarChart
                            height={chartHeightFor(isNarrow)}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    data: months,
                                    tickLabelInterval: monthTickInterval,
                                    tickLabelStyle: tickLabelStyleFor(isNarrow),
                                    categoryGapRatio: CATEGORY_GAP_RATIO,
                                    barGapRatio: BAR_GAP_RATIO,
                                },
                            ]}
                            // Headroom from the visible series only, so switching off the
                            // tallest lets the rest use the height.
                            yAxis={[
                                {
                                    max: axisMaxWithHeadroom(visible.flatMap(series => series.data)),
                                },
                            ]}
                            series={visible.map(series => ({
                                ...series,
                                valueFormatter: (value: number | null): string =>
                                    formatMoney(value ?? 0, translate),
                            }))}
                            // Both are required: the function prints the zeros the built-in
                            // shortcut suppresses, and the slot is what puts the text above
                            // the bar instead of inside it.
                            barLabel={barValueLabel(value => compactMoney(value, i18n))}
                            slots={{ barLabel: AboveBarLabel }}
                            slotProps={{ barLabel: { labelFontSize: labelFontSizeFor(isNarrow) } as never }}
                            borderRadius={BAR_BORDER_RADIUS}
                            hideLegend
                        />
                    </ChartScroller>
                </>
            )}
        </Card>
    );
};
